import { Box, Card, CircularProgress, Typography } from '@mui/material';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Empty,
  ListContextProvider,
  ListView,
  ListViewProps,
  SortProps,
} from 'react-admin';
import styled from 'styled-components';

import useInfiniteListController from './useInfiniteListController';

type PaginationProps = {
  perPage?: number;
  disableSyncWithLocation?: boolean;
  filterDefaultValues?: Record<string, unknown>;
  // Permanent filter — always applied, not shown in the filter bar or clearable.
  filter?: Record<string, unknown>;
  storeKey?: string;
};

type InfiniteListProps = ListViewProps & PaginationProps & Partial<SortProps>;

type InfiniteLoaderProps = {
  className?: string;
  isMore?: boolean;
  isLoading?: boolean;
  onLoadMore?: () => void;
};

const InfiniteLoader = styled(
  ({
    className,
    isMore,
    isLoading,
    onLoadMore = noop,
  }: InfiniteLoaderProps) => {
    // Debounce isLoading (i.e., during initial page loads when multiple pages might get loaded to fill the viewport)
    // This keeps the text from switching constantly, but also (and more importantly) makes this easier to test
    const [isLoadingDebounced, setIsLoading] = useState(isLoading);

    const setIsLoadingDebounced = useCallback(debounce(setIsLoading, 100), []);

    useEffect(() => {
      setIsLoadingDebounced(isLoading);
    }, [isLoading, setIsLoadingDebounced]);

    const loadMore = isMore ? onLoadMore : noop;

    // Observer
    const observerElem = useRef(null);

    const handleObserver = useCallback(
      (entries: any[]) => {
        const [target] = entries;

        if (target.isIntersecting && isMore && !isLoading) {
          loadMore();
        }
      },
      [loadMore, isMore, isLoading],
    );

    useEffect(() => {
      const element = observerElem.current;

      if (!element) {
        return noop;
      }

      const observer = new IntersectionObserver(handleObserver, {
        threshold: 0,
      });

      observer.observe(element);

      return () => observer.unobserve(element);
    }, [handleObserver]);

    if (!isMore) {
      return null;
    }

    return (
      <div className={className} ref={observerElem}>
        <Button
          size="large"
          color="inherit"
          data-testid="infinite-loader"
          disabled={isLoadingDebounced || !isMore}
          // Allow clicks as a backup
          onClick={() => loadMore()}
          label={isLoadingDebounced ? 'Loading...' : 'Load More'}
        />
      </div>
    );
  },
)`
  display: flex;
  justify-content: center;
  padding: 20px;
`;

const InfiniteList: FC<InfiniteListProps> = (props) => {
  const {
    perPage,
    sort,
    disableSyncWithLocation,
    filterDefaultValues,
    filter,
    storeKey,
    empty,
    ...listViewProps
  } = props;
  const listContext = useInfiniteListController({
    perPage,
    sort,
    disableSyncWithLocation,
    filterDefaultValues,
    filter,
    storeKey,
  });
  const { hasNextPage, isFetching, isLoading, fetchNextPage, total } =
    listContext;

  return (
    <ListContextProvider value={listContext}>
      <ListView
        {...listViewProps}
        empty={
          // Preserve an explicit `empty` from the caller (including `false` to
          // keep the filter bar mounted when the filtered result is empty).
          empty ??
          (isLoading ? (
            <Box display="flex" justifyContent="center" py={10}>
              <CircularProgress />
            </Box>
          ) : (
            <Empty />
          ))
        }
        pagination={
          <>
            <InfiniteLoader
              isMore={!!hasNextPage}
              isLoading={isFetching || isLoading}
              onLoadMore={fetchNextPage}
            />
            {(total || 0) > 0 && (
              <Box sx={{ position: 'sticky', bottom: 20, textAlign: 'center' }}>
                <Card
                  elevation={2}
                  sx={{ px: 2, py: 1, mb: 1, mt: 2, display: 'inline-block' }}
                >
                  <Typography variant="body2">
                    {total?.toLocaleString()} results
                  </Typography>
                </Card>
              </Box>
            )}
          </>
        }
      />
    </ListContextProvider>
  );
};

export default InfiniteList;
